// src/services/projectService.js (Enhanced version)
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  arrayUnion,
  arrayRemove,
  or
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

export class ProjectService {
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // Create project
  static async createProject(projectData) {
    const userId = this.ensureAuthenticated();

    try {
      const docRef = await addDoc(collection(db, 'taskdata-projects'), {
        ...projectData,
        status: projectData.status || 'active',
        createdBy: userId,
        participants: [userId],
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating project:', error);
      throw new Error('Failed to create project');
    }
  }

  // Update project
  static async updateProject(projectId, projectData) {
    const userId = this.ensureAuthenticated();

    try {
      // Check if user has permission to update
      const project = await this.getProject(projectId);
      if (!project || (project.createdBy !== userId && !project.participants?.includes(userId))) {
        throw new Error('Permission denied');
      }

      await updateDoc(doc(db, 'taskdata-projects', projectId), {
        ...projectData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating project:', error);
      throw new Error('Failed to update project');
    }
  }

  // Delete project
  static async deleteProject(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      if (!project || project.createdBy !== userId) {
        throw new Error('Permission denied');
      }

      await deleteDoc(doc(db, 'taskdata-projects', projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project');
    }
  }

  // Get single project
  static async getProject(projectId) {
    try {
      const docRef = doc(db, 'taskdata-projects', projectId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
          startDate: data.startDate?.toDate ? data.startDate.toDate() : data.startDate,
          endDate: data.endDate?.toDate ? data.endDate.toDate() : data.endDate
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw new Error('Failed to fetch project');
    }
  }

  // Get user's projects
  static async getUserProjects() {
    const userId = this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, 'taskdata-projects'),
        or(
          where('createdBy', '==', userId),
          where('participants', 'array-contains', userId)
        ),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
          startDate: data.startDate?.toDate ? data.startDate.toDate() : data.startDate,
          endDate: data.endDate?.toDate ? data.endDate.toDate() : data.endDate
        };
      });
    } catch (error) {
      console.error('Error fetching user projects:', error);
      throw new Error('Failed to fetch projects');
    }
  }

  // Add participant to project
  static async addParticipantToProject(projectId, userId) {
    try {
      await updateDoc(doc(db, 'taskdata-projects', projectId), {
        participants: arrayUnion(userId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error adding participant:', error);
      throw new Error('Failed to add participant');
    }
  }

  // Remove participant from project
  static async removeParticipantFromProject(projectId, userId) {
    try {
      await updateDoc(doc(db, 'taskdata-projects', projectId), {
        participants: arrayRemove(userId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error removing participant:', error);
      throw new Error('Failed to remove participant');
    }
  }

  // Search projects by criteria
  static async searchProjects(searchTerm, filters = {}) {
    const userId = this.ensureAuthenticated();

    try {
      let q = query(
        collection(db, 'taskdata-projects'),
        or(
          where('createdBy', '==', userId),
          where('participants', 'array-contains', userId)
        )
      );

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.province) {
        q = query(q, where('province', '==', filters.province));
      }

      const querySnapshot = await getDocs(q);
      let projects = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      });

      // Client-side filtering for text search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        projects = projects.filter(project => 
          project.name.toLowerCase().includes(term) ||
          project.description?.toLowerCase().includes(term) ||
          project.district?.toLowerCase().includes(term)
        );
      }

      return projects;
    } catch (error) {
      console.error('Error searching projects:', error);
      throw new Error('Failed to search projects');
    }
  }

  static async searchUsersByLocation(province, district, searchTerm = '') {
    try {
      let q = query(
        collection(db, 'taskdata-users'),
        where('province', '==', province)
      );

      // If district is provided, add district filter
      if (district) {
        q = query(q, where('district', '==', district));
      }

      const querySnapshot = await getDocs(q);
      let users = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));

      // Client-side filtering by search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        users = users.filter(user => 
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.schoolName?.toLowerCase().includes(term) ||
          user.projectName?.toLowerCase().includes(term)
        );
      }

      return users;
    } catch (error) {
      console.error('Error searching users by location:', error);
      throw new Error('Failed to search users');
    }
  }

  // Add multiple users to project
  static async addUsersToProject(projectId, userIds) {
    const currentUserId = this.ensureAuthenticated();

    try {
      // Verify current user has permission to add users
      const project = await this.getProject(projectId);
      if (project.createdBy !== currentUserId && !project.participants?.includes(currentUserId)) {
        throw new Error('Permission denied: You cannot add users to this project');
      }

      // Get current participants to avoid duplicates
      const currentParticipants = project.participants || [];
      
      // Filter out users already in project
      const newUserIds = userIds.filter(userId => !currentParticipants.includes(userId));
      
      if (newUserIds.length === 0) {
        throw new Error('All selected users are already in the project');
      }

      // Add users to project
      const projectRef = doc(db, 'taskdata-projects', projectId);
      await updateDoc(projectRef, {
        participants: arrayUnion(...newUserIds),
        updatedAt: new Date()
      });

      return newUserIds.length;
    } catch (error) {
      console.error('Error adding users to project:', error);
      throw error;
    }
  }

  // Remove user from project
  static async removeUserFromProject(projectId, userId) {
    const currentUserId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Only project creator or the user themselves can remove participation
      if (project.createdBy !== currentUserId && userId !== currentUserId) {
        throw new Error('Permission denied');
      }

      const projectRef = doc(db, 'taskdata-projects', projectId);
      await updateDoc(projectRef, {
        participants: arrayRemove(userId),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error removing user from project:', error);
      throw error;
    }
  }

  // Get project participants with their details
  static async getProjectParticipantsWithDetails(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Verify user has access to project
      if (!project.participants?.includes(userId) && project.createdBy !== userId) {
        throw new Error('Permission denied: You cannot view project participants');
      }

      if (!project.participants || project.participants.length === 0) {
        return [];
      }

      // Get user details for all participants
      const participantDetails = [];
      for (const participantId of project.participants) {
        try {
          const userDoc = await getDoc(doc(db, 'taskdata-users', participantId));
          if (userDoc.exists()) {
            participantDetails.push({
              userId: userDoc.id,
              ...userDoc.data()
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch details for user ${participantId}:`, err);
        }
      }

      return participantDetails;
    } catch (error) {
      console.error('Error fetching project participants with details:', error);
      throw error;
    }
  }

  // Delete project (only creator can delete)
  static async deleteProject(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Only creator can delete project
      if (project.createdBy !== userId) {
        throw new Error('Permission denied: Only project creator can delete project');
      }

      await deleteDoc(doc(db, 'taskdata-projects', projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  // Archive project
  static async archiveProject(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Only creator can archive project
      if (project.createdBy !== userId) {
        throw new Error('Permission denied: Only project creator can archive project');
      }

      await this.updateProject(projectId, { status: 'archived' });
    } catch (error) {
      console.error('Error archiving project:', error);
      throw error;
    }
  }

  // Reactivate project
  static async reactivateProject(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Only creator can reactivate project
      if (project.createdBy !== userId) {
        throw new Error('Permission denied: Only project creator can reactivate project');
      }

      await this.updateProject(projectId, { status: 'active' });
    } catch (error) {
      console.error('Error reactivating project:', error);
      throw error;
    }
  }

  // Search projects with advanced filters
  static async searchProjects(searchTerm, filters = {}) {
    const userId = this.ensureAuthenticated();

    try {
      // Start with base query for user's projects
      let q = query(
        collection(db, 'taskdata-projects'),
        or(
          where('createdBy', '==', userId),
          where('participants', 'array-contains', userId)
        )
      );

      // Apply status filter if provided
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      // Apply province filter if provided
      if (filters.province) {
        q = query(q, where('province', '==', filters.province));
      }

      const querySnapshot = await getDocs(q);
      let projects = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      });

      // Client-side filtering for search term and other filters
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        projects = projects.filter(project => 
          project.name?.toLowerCase().includes(term) ||
          project.description?.toLowerCase().includes(term) ||
          project.district?.toLowerCase().includes(term)
        );
      }

      // Filter by district if provided
      if (filters.district) {
        projects = projects.filter(project => project.district === filters.district);
      }

      return projects;
    } catch (error) {
      console.error('Error searching projects:', error);
      throw new Error('Failed to search projects');
    }
  }

  // Get user's role in project
  static async getUserRoleInProject(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      if (project.createdBy === userId) {
        return 'creator';
      } else if (project.participants?.includes(userId)) {
        return 'participant';
      } else {
        return null; // No access
      }
    } catch (error) {
      console.error('Error getting user role in project:', error);
      throw error;
    }
  }

  static async searchUsersByLocation(projectData, searchTerm = '') {
    const userId = this.ensureAuthenticated();
    
    try {
      let q = collection(db, 'taskdata-users');
      const queries = [];
      
      // Build location-based queries based on project level
      switch (projectData.projectLevel) {
        case 'Provincial':
          // Search all users in the same province
          if (projectData.province) {
            queries.push(
              query(q, 
                where('province', '==', projectData.province),
                where('profileCompleted', '==', true)
              )
            );
          }
          break;
          
        case 'District':
          // Search users in the same district
          if (projectData.district && projectData.province) {
            queries.push(
              query(q,
                where('province', '==', projectData.province),
                where('district', '==', projectData.district),
                where('profileCompleted', '==', true)
              )
            );
          }
          break;
          
        case 'Circuit':
          // Search users in the same circuit
          if (projectData.circuit && projectData.district && projectData.province) {
            queries.push(
              query(q,
                where('province', '==', projectData.province),
                where('district', '==', projectData.district),
                where('circuit', '==', projectData.circuit),
                where('profileCompleted', '==', true)
              )
            );
          }
          break;
          
        case 'School':
          // Search users in the same school
          if (projectData.memberSchools && projectData.memberSchools.length > 0) {
            // Search for users in any of the member schools
            projectData.memberSchools.forEach(school => {
              queries.push(
                query(q,
                  where('province', '==', projectData.province),
                  where('district', '==', projectData.district),
                  where('schoolName', '==', school.name),
                  where('profileCompleted', '==', true)
                )
              );
            });
          }
          break;
      }

      // Execute all queries and combine results
      const allUsers = new Map(); // Use Map to deduplicate
      
      for (const userQuery of queries) {
        try {
          const querySnapshot = await getDocs(userQuery);
          querySnapshot.docs.forEach(doc => {
            const userData = doc.data();
            // Exclude current user and ensure they're not already a participant
            if (doc.id !== userId && !projectData.participants?.includes(doc.id)) {
              allUsers.set(doc.id, {
                id: doc.id,
                ...userData
              });
            }
          });
        } catch (error) {
          console.error('Error in user query:', error);
        }
      }

      let users = Array.from(allUsers.values());

      // Apply search term filter if provided
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        users = users.filter(user => {
          const firstName = (user.firstName || '').toLowerCase();
          const lastName = (user.lastName || '').toLowerCase();
          const email = (user.email || '').toLowerCase();
          const position = (user.position || '').toLowerCase();
          const schoolName = (user.schoolName || '').toLowerCase();
          
          return firstName.includes(term) ||
                lastName.includes(term) ||
                email.includes(term) ||
                position.includes(term) ||
                schoolName.includes(term);
        });
      }

      // Sort users by name
      users.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return nameA.localeCompare(nameB);
      });

      return users;
    } catch (error) {
      console.error('Error searching users by location:', error);
      throw new Error('Failed to search users');
    }
  }

  // Get project participants with full user details
  static async getProjectParticipantsDetails(projectId) {
    const userId = this.ensureAuthenticated();

    try {
      const project = await this.getProject(projectId);
      
      // Verify user has access to project
      if (!project.participants?.includes(userId) && project.createdBy !== userId) {
        throw new Error('Permission denied: You cannot view project participants');
      }

      if (!project.participants || project.participants.length === 0) {
        return [];
      }

      // Fetch user details for all participants
      const participantDetails = [];
      for (const participantId of project.participants) {
        try {
          const userRef = doc(db, 'taskdata-users', participantId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            participantDetails.push({
              id: participantId,
              ...userSnap.data()
            });
          }
        } catch (error) {
          console.error(`Error fetching participant ${participantId}:`, error);
        }
      }

      return participantDetails;
    } catch (error) {
      console.error('Error fetching project participants details:', error);
      throw error;
    }
  }
}